import type { JoinMethod } from '@orbit/api-types'
import { isNoConnection, statusOf } from '@lib/apiError'
import { tripPreviewMeta } from '@lib/joinRequests'
import { countLabel } from '@/utils/plural'
import { capitalizedFirstName } from '@/utils/userNames'

/**
 * The Join page's pure core: what the invitation says, which action the page
 * offers, and the path/method plumbing between the code-entry modal and the
 * page. No React, no network — tests run in node.
 */

/** The public `joinPreview` response, narrowed to what the page renders. */
export interface JoinPreview {
	tripInfo: { code: string; name: string; startDate: string | null; dayCount: number }
	tripOwner: { firstName: string; lastName: string; profilePicture: string | null }
	/** Active members already on the trip, the inviter included. */
	memberCount: number
}

/** Everything the invitation panel shows, already worded. */
export interface JoinInvitation {
	tripName: string
	inviterName: string
	inviterInitial: string
	inviterAvatarUrl: string | null
	/** "Apr 19 – May 4, 2026 · 16 days", each part dropped when the trip lacks it; null with neither. */
	meta: string | null
	/** "Alice and 4 others are going". */
	goingLabel: string
	/** "You’ll be added to the trip once Alice approves your request" — once the join became a Join request. */
	requestSentDetail: string
}

const FALLBACK_INVITER = 'Someone'

const goingLabel = (inviter: string, memberCount: number): string => {
	const others = Math.max(memberCount - 1, 0)
	if (others === 0) return `${inviter} is going`
	return `${inviter} and ${countLabel(others, 'other')} are going`
}

export const buildJoinInvitation = (preview: JoinPreview): JoinInvitation => {
	// Profiles arrive as typed ("andres"); the invitation always opens with a capital.
	const ownerName = capitalizedFirstName(preview.tripOwner.firstName)
	const inviterName = ownerName ?? FALLBACK_INVITER
	const { startDate, dayCount } = preview.tripInfo

	return {
		tripName: preview.tripInfo.name,
		inviterName,
		inviterInitial: ownerName ? ownerName.charAt(0) : '?',
		inviterAvatarUrl: preview.tripOwner.profilePicture ?? null,
		meta: tripPreviewMeta(startDate, dayCount),
		goingLabel: goingLabel(inviterName, preview.memberCount),
		requestSentDetail: `You’ll be added to the trip once ${ownerName ?? 'the owner'} approves your request`,
	}
}

/** How the person reached the join — the wire enum, shared with the server. */
export type { JoinMethod }

const VIA_PARAM = 'via'

/** In-app path of the Join page for a code; `code` marks a hand-typed entry. */
export const joinTripPath = (tripCode: string, method: JoinMethod = 'link'): string => {
	const path = `/join/${encodeURIComponent(tripCode.trim().toUpperCase())}`
	return method === 'code' ? `${path}?${VIA_PARAM}=code` : path
}

export const joinMethodFromSearch = (search: string): JoinMethod =>
	new URLSearchParams(search).get(VIA_PARAM) === 'code' ? 'code' : 'link'

/** What the page knows about the visitor's membership of the previewed trip. */
export type MembershipStatus = 'loading' | 'member' | 'not-member' | 'unknown'

/** Whether the signed-in visitor has given their name yet. */
export type ProfileNameStatus = 'loading' | 'missing' | 'set'

/**
 * What the right-hand panel does: `join` runs on its own, the others wait on the
 * person. `name` asks a nameless visitor for their name first, so the owner never
 * sees a blank member or Join request.
 */
export type JoinAction = 'sign-in' | 'checking' | 'name' | 'join' | 'open'

export const deriveJoinAction = (input: {
	signedIn: boolean
	membership: MembershipStatus
	/** Required: a page that left it out would skip the name step without a word. */
	profileName: ProfileNameStatus
}): JoinAction => {
	if (!input.signedIn) return 'sign-in'
	switch (input.membership) {
		case 'loading':
			return 'checking'
		case 'member':
			return 'open'
		default:
			// A failed membership lookup must not block the join: the server
			// answers 409 if they are already in, and the page handles that.
			if (input.profileName === 'loading') return 'checking'
			if (input.profileName === 'missing') return 'name'
			return 'join'
	}
}

/** The visitor's trip matching the invitation's code, if they are already in it. */
export const isMemberOf = <T extends { code: string }>(trips: T[], tripCode: string): T | null => {
	const wanted = tripCode.trim().toUpperCase()
	return trips.find(trip => trip.code.toUpperCase() === wanted) ?? null
}

/** What the page knows about membership, from the state of its trips query. */
export const deriveMembership = <T extends { code: string }>(input: {
	signedIn: boolean
	/** The trips query is in flight with nothing cached yet. */
	isLoading: boolean
	trips: T[] | undefined
	tripCode: string | undefined
}): { status: MembershipStatus; memberTrip: T | null } => {
	if (!input.signedIn) return { status: 'unknown', memberTrip: null }
	if (input.isLoading) return { status: 'loading', memberTrip: null }
	if (!input.trips || !input.tripCode) return { status: 'unknown', memberTrip: null }
	const memberTrip = isMemberOf(input.trips, input.tripCode)
	return { status: memberTrip ? 'member' : 'not-member', memberTrip }
}

/**
 * What a refused join says, in our words (iOS `TripJoinPresentation.joinRefusal`).
 * A 409 and a Join request are answered before this, so neither reaches it.
 */
export const joinRefusal = (error: unknown): string => {
	if (isNoConnection(error)) return 'Unable to join the trip. Please check your internet connection and try again'
	const status = statusOf(error)
	if (status === 400 || status === 404) return "We couldn't find that trip. Please double check the invitation and try again"
	return 'Unable to join the trip, please try again later'
}
